// ── THE CANONICAL FARM × CROP × CROP-YEAR STRATEGY OBJECT ────────────────────
//
// One object per (farm, crop, crop year). Four domains, each supplied by the
// engine that owns it, reconciled and validated here, and RENDERED by a report
// that computes nothing.
//
//     FSA        ARC-CO / PLC lean            — arcPlcRec / the ARC-PLC engine
//     INDIVIDUAL MPCI coverage + unit + APH   — wheat/coverageRecommend.js
//     AREA       SCO / ECO bands + fit        — wheat/areaFit.js (via the above)
//     FUTURES    a simplified price plan      — grainTargets.js
//
// ── WHY THIS EXISTS ──────────────────────────────────────────────────────────
//
// The Full Strategy Report was self-contained by design, and its own note in
// CLAUDE.md records what that cost: `caArcPlcCountyRec` "MIRRORS
// CoverageAdvisorView.arcPlcRec — keep the formula in sync". A second copy of a
// recommendation, kept in step by hand, is the exact pattern this repo has
// already paid for three times (two guarantee engines, three news handlers,
// three normFarmerName copies). A REPORT is the worst place for it: it is the
// document the grower actually holds, so the copy that drifts is the one that
// leaves the building.
//
// So this module RECONCILES; it does not decide. Every domain arrives already
// computed. The one thing it adds is disagreement detection.
//
// ⚠ IT NEVER FILLS A MISSING INPUT WITH A PLAUSIBLE VALUE. A domain that was
// not supplied is `null` with a stated reason, the strategy state degrades, and
// the page prints the gap. That is the whole contract — a strategy document
// that quietly invents an FSA lean or an APH is worse than one that says it
// does not have it.

export const STRATEGY_STATE = {
  READY: "READY",
  WARN: "READY WITH WARNINGS",
  INCOMPLETE: "INCOMPLETE",
};

export const SEVERITY = { BLOCK: "block", WARN: "warn", NOTE: "note" };

// ── THE RISK STACK ───────────────────────────────────────────────────────────
// Four tools, four different risks. They are listed, never summed.
//
// ⚠ NEVER ADD THEIR PERCENTAGES OR DOLLARS INTO ONE "TOTAL PROTECTION". They
// cover different perils on different triggers with different settlement dates:
// 80% MPCI + SCO to 90% + ECO to 95% + 50% of the crop forward sold is not
// "265% protected", and the arithmetic that produces such a number is the same
// arithmetic that tells a grower area coverage is individual coverage.
export const RISK_STACK = [
  { key: "fsa", label: "FSA (ARC-CO / PLC)", risk: "Government-program revenue and price support", trigger: "County / national, program year" },
  { key: "individual", label: "MPCI", risk: "This farm's own yield and revenue", trigger: "Your farm's APH and guarantee" },
  { key: "area", label: "SCO / ECO", risk: "County-area revenue", trigger: "County index — can pay when you did not lose, and miss when you did" },
  { key: "futures", label: "Futures targets", risk: "Market price exposure on bushels you will sell", trigger: "The board, when your order fills" },
];

const isNum = (v) => v != null && isFinite(Number(v));
const r2 = (n) => (isNum(n) ? Math.round(Number(n) * 100) / 100 : null);

// ── the consistency engine ───────────────────────────────────────────────────
//
// Each check answers one question and returns null when it passes. A check that
// cannot RUN returns a NOTE saying so — "we did not look" and "we looked and it
// was fine" are different facts, and collapsing them is how a green report comes
// to mean nothing.
export function checkConsistency(s) {
  const f = [];
  const add = (severity, key, message, detail) => f.push({ severity, key, message, detail: detail || null });

  // 1. the page must agree with the engine that produced it
  if (s.individual) {
    if (!isNum(s.individual.coverageLevel)) add(SEVERITY.BLOCK, "individual-level-missing", "The individual coverage recommendation carries no level.");
    if (s.individual.sourceEngine == null) add(SEVERITY.WARN, "individual-source-unnamed", "The individual recommendation does not name the engine that produced it.");
    if (isNum(s.individual.sourceCoverageLevel) &&
        Math.abs(Number(s.individual.sourceCoverageLevel) - Number(s.individual.coverageLevel)) > 1e-9)
      add(SEVERITY.BLOCK, "individual-disagrees", "The report's coverage level does not match the engine's.",
          "report " + s.individual.coverageLevel + " vs engine " + s.individual.sourceCoverageLevel);
  } else add(SEVERITY.BLOCK, "individual-missing", "No individual coverage recommendation was supplied.");

  // 2. APH vintage / provenance
  if (s.individual) {
    if (!s.individual.aphProvenance) add(SEVERITY.WARN, "aph-provenance-unknown", "The APH's provenance is not stated.");
    if (isNum(s.individual.aphCropYear) && isNum(s.cropYear) &&
        Number(s.individual.aphCropYear) !== Number(s.cropYear))
      add(SEVERITY.WARN, "aph-vintage", "The APH is from a different crop year than this strategy.",
          "APH " + s.individual.aphCropYear + " vs strategy " + s.cropYear);
  }

  // 3/4. crop year and actuarial year
  for (const [k, dom] of [["individual", s.individual], ["area", s.area], ["futures", s.futures], ["fsa", s.fsa]]) {
    if (dom && isNum(dom.cropYear) && isNum(s.cropYear) && Number(dom.cropYear) !== Number(s.cropYear))
      add(SEVERITY.BLOCK, k + "-crop-year", "The " + k + " section is for a different crop year.",
          k + " " + dom.cropYear + " vs strategy " + s.cropYear);
  }
  if (s.individual && isNum(s.individual.actuarialYear) && isNum(s.cropYear) &&
      Number(s.individual.actuarialYear) !== Number(s.cropYear))
    add(SEVERITY.BLOCK, "actuarial-year", "The rates are from a different actuarial year than the crop year.",
        "actuarials " + s.individual.actuarialYear + " vs crop year " + s.cropYear);

  // 5. the SCO/ECO offer must actually exist for this type/practice/county
  if (s.area && s.area.elected && s.area.offerExists === false)
    add(SEVERITY.BLOCK, "area-offer-missing", "SCO/ECO is shown as elected but RMA publishes no offer for this county, type and practice.",
        s.area.offerReason || null);

  // 6. the 2027 SCO band
  if (s.area && isNum(s.area.scoTopPct) && isNum(s.cropYear) && Number(s.cropYear) >= 2027 &&
      Number(s.area.scoTopPct) !== 90)
    add(SEVERITY.BLOCK, "sco-band-legacy", "SCO is shown topping at " + s.area.scoTopPct +
        "% for CY" + s.cropYear + "; the 2027 endorsement reaches 90%.");

  // 7. ARC no longer excludes SCO
  if (s.fsa && s.fsa.arcBlocksSco === true)
    add(SEVERITY.BLOCK, "arc-sco-incompatible", "This strategy still applies the retired rule that an ARC election bars SCO. SCO may be purchased regardless of the FSA ARC election.");

  // 8. an MYA forecast is not a final
  if (s.fsa && s.fsa.myaStatus && /final/i.test(String(s.fsa.myaStatus)) && s.fsa.myaIsForecast === true)
    add(SEVERITY.BLOCK, "mya-mislabelled", "An MYA forecast is labelled final.");
  if (s.fsa && s.fsa.myaIsForecast === true && s.fsa.myaLabelled !== true)
    add(SEVERITY.WARN, "mya-unlabelled", "The MYA figure is a forecast and is not labelled as one.");

  // 9. thin area history must not read as a strong fit
  if (s.area && s.area.fitState === "INSUFFICIENT_HISTORY" && /strong/i.test(String(s.area.fitLabel || "")))
    add(SEVERITY.BLOCK, "area-fit-overstated", "The area fit is labelled strong on insufficient history.");

  // 10. the grain plan must be sized off the SAME production assumption
  if (s.futures && s.individual) {
    if (s.futures.quantitiesShown && s.futures.productionBasis == null)
      add(SEVERITY.BLOCK, "futures-basis-missing", "Bushel quantities are shown with no stated production basis.");
    if (s.futures.productionBasis && s.individual.productionBasis &&
        s.futures.productionBasis !== s.individual.productionBasis)
      add(SEVERITY.WARN, "futures-basis-differs", "The futures plan and the coverage recommendation are sized off different production assumptions.",
          s.futures.productionBasis + " vs " + s.individual.productionBasis);
  }

  // 11. SCO must be computed on the coverage level actually recommended
  if (s.area && s.individual && isNum(s.area.baseCoverageLevel) && isNum(s.individual.coverageLevel) &&
      Math.abs(Number(s.area.baseCoverageLevel) - Number(s.individual.coverageLevel)) > 1e-9)
    add(SEVERITY.BLOCK, "sco-base-mismatch", "SCO is priced on a different base coverage level than the one recommended.",
        "SCO base " + s.area.baseCoverageLevel + " vs recommended " + s.individual.coverageLevel);

  // 12. A PREMIUM MAY NOT BE QUOTED ON A UNIT STRUCTURE NOBODY STATED.
  // `wheat/coverageRecommend` falls back to (acres >= 20 ? "EU" : "BU") when the
  // policy states none, so it can price a comparison at all — and it reports that
  // honestly as `unitSource`. Deriving one to CALCULATE with is fine; printing a
  // dollar figure that rests on it is not, because the BU->EU flip moves the
  // 75->80 cost 27% (measured, and recorded in that engine's own comment). So the
  // derivation is allowed and the PRICE built on it is refused.
  // Only "policy" (the AIP's own file) and "caller" (a human chose it) are stated.
  if (s.individual && s.individual.unitSource &&
      !["policy", "caller"].includes(String(s.individual.unitSource)) &&
      isNum(s.individual.premiumPerAc))
    add(SEVERITY.BLOCK, "premium-on-derived-unit",
        "A premium is quoted on a unit structure that was derived from acreage rather than stated on the policy.",
        "unitSource " + s.individual.unitSource + ", unit " + (s.individual.unitStructure || "?"));

  // domains that were not supplied at all — a NOTE, because the page must say so
  for (const [k, dom] of [["fsa", s.fsa], ["area", s.area], ["futures", s.futures]])
    if (!dom) add(SEVERITY.NOTE, k + "-absent", "No " + k.toUpperCase() + " section was supplied for this crop.");

  return f;
}

export function stateFromFindings(findings) {
  if (findings.some((x) => x.severity === SEVERITY.BLOCK)) return STRATEGY_STATE.INCOMPLETE;
  if (findings.some((x) => x.severity === SEVERITY.WARN)) return STRATEGY_STATE.WARN;
  return STRATEGY_STATE.READY;
}

// ── the builder ──────────────────────────────────────────────────────────────
//
// Domains arrive COMPUTED. Nothing here decides anything; the only outputs it
// creates are the reconciliation findings, the state, and the one-line summary
// row the first page prints.
// ⚠ `needs` ARE INPUTS ONLY THE READER CAN SUPPLY, and they are passed in
// EXPLICITLY. Page one used to print "the premium comparison it is judged on
// cannot be computed without an acreage" under the heading WHAT WOULD CHANGE
// OUR MIND — a failure message wearing a decision label. A missing input is
// neither a reason for the recommendation nor a thing that would reverse it;
// it is a request, and it belongs in a block addressed to the grower.
//
// Explicit rather than inferred: sniffing the narrative for "cannot be
// computed" would reclassify a genuine reason the day someone rewords it.
// ⚠ A STRATEGY IS PER UNIT, AND PAGE ONE PRINTED THEM ALL UNDER ONE HEADING.
// The renderer's own comment said "one block per crop"; the caller builds one per
// (county x practice). Craig Metzler's CY2027 report printed THREE blocks all
// headed "WHEAT - 2027 - what we recommend", carrying $21.73, $18.40 and $17.34
// per acre against 66, 20 and 100 acres, with nothing on the page to say which
// county any of them belonged to. Measured over the CY2027 wheat book: 68 of 120
// farms print more than one block, worst case 5, and 65 of those span counties.
//
// `scope` is what the block is FOR. It is carried, never derived — this module
// does no lookups — and a caller that omits it gets exactly the old behaviour.
export function buildFarmStrategy({ farm, crop, cropYear, fsa = null, individual = null, area = null, futures = null, needs = [], scope = null }) {
  const s = { farm: farm || null, crop: crop || null, cropYear: cropYear != null ? Number(cropYear) : null,
              scope: scope && (scope.county || scope.state) ? { county: scope.county || null, state: scope.state || null } : null,
              fsa, individual, area, futures };
  const findings = checkConsistency(s);
  const state = stateFromFindings(findings);

  // The page-one row. Every cell is a value one of the engines produced, or a
  // dash — there is no fallback and no derived label.
  const summary = {
    crop: s.crop,
    fsa: fsa && fsa.lean ? fsa.lean : "—",
    individual: individual && isNum(individual.coverageLevel)
      ? Math.round(individual.coverageLevel * 100) + "% " + (individual.plan || "RP") +
        (individual.unitStructure ? " " + individual.unitStructure : "")
      : "—",
    area: area && area.elected
      ? [area.scoElected ? "SCO" + (isNum(area.scoTopPct) ? area.scoTopPct : "") : null,
         area.ecoElected ? "ECO" + (isNum(area.ecoTopPct) ? area.ecoTopPct : "") : null]
        .filter(Boolean).join(" + ") || "—"
      : "—",
    futures: futures && futures.contractLabel && (futures.targets || []).length
      ? futures.contractLabel + ": " + futures.targets.map((t) => (isNum(t.price) ? "$" + Number(t.price).toFixed(2) : "—")).join(" / ")
      : "—",
  };

  return {
    ...s,
    summary,
    findings,
    state,
    blocking: findings.filter((x) => x.severity === SEVERITY.BLOCK),
    warnings: findings.filter((x) => x.severity === SEVERITY.WARN),
    notes: findings.filter((x) => x.severity === SEVERITY.NOTE),
    riskStack: RISK_STACK,
    // What the grower has to give us before the blank figures above can be filled.
    needs: Array.isArray(needs) ? needs.filter((x) => x && x.what) : [],
    // ⚠ the four domains are NEVER combined into one number
    totalProtectionPct: null,
    totalProtectionNote: "Each tool covers a different risk on a different trigger. Their percentages are not additive and no combined figure is shown.",
  };
}

// ── the four page-one lines, assembled from what the engines said ────────────
//
// WHY / COST / MAIN TRADEOFF / WHAT WOULD CHANGE OUR MIND. Each is taken from a
// field an engine populated; where the engine said nothing, so does this.
export function strategyNarrative(s) {
  const ind = s.individual, ar = s.area;
  const why = [];
  if (ind && ind.headline) why.push(ind.headline);
  if (s.fsa && s.fsa.headline) why.push(s.fsa.headline);
  const cost = [];
  if (ind && isNum(ind.premiumPerAc)) cost.push("MPCI " + fmt$(ind.premiumPerAc) + "/ac");
  if (ar && isNum(ar.premiumPerAc)) cost.push("SCO/ECO " + fmt$(ar.premiumPerAc) + "/ac");
  if (ind && ar && isNum(ind.premiumPerAc) && isNum(ar.premiumPerAc))
    cost.push("total " + fmt$(Number(ind.premiumPerAc) + Number(ar.premiumPerAc)) + "/ac");
  return {
    why: why.length ? why.join(" ") : null,
    cost: cost.length ? cost.join(" · ") : null,
    // the marginal move is the tradeoff — supplied by the coverage engine
    tradeoff: ind && ind.mainTradeoff ? ind.mainTradeoff : null,
    changeOurMind: ind && ind.changeOurMind ? ind.changeOurMind : null,
  };
}
function fmt$(n) { return isNum(n) ? "$" + Number(n).toFixed(2) : "—"; }
export const roundMoney = r2;
